import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { NavLink } from "react-router-dom";
import { ReactComponent as Home } from "../../assets/images/Home.svg";

import DataTable from "../../components/table/Datatable";
import StatusPill from "../../components/table/StatusPill";
import PageHeader from "../../components/layout/PageHeader";

import DateRangeButton from "../../components/inputs/DateRangeButton";
import StatusSelect from "../../components/inputs/StatusSelect";
import PaginationFooter from "../../components/table/PaginationFooter";

import { createHttpClient } from "../../api/client";

function compareText(a, b) {
  const A = String(a ?? "").toLowerCase();
  const B = String(b ?? "").toLowerCase();
  if (A < B) return -1;
  if (A > B) return 1;
  return 0;
}

function toISODateOnly(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function asText(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(", ");
  if (typeof v === "object") return v.name ?? v.label ?? v.title ?? v.value ?? (v.id != null ? String(v.id) : "");
  return String(v);
}

// map product_status -> label ที่ StatusPill ของคุณรับ (ถ้ารับเป็นไทย)
// ถ้า StatusPill ของคุณรองรับอังกฤษอยู่แล้ว ตัด mapping นี้ได้
function statusLabelFromProductStatus(ps) {
  const s = String(ps || "").toLowerCase();
  if (s === "draft") return "ร่าง";
  if (s === "available") return "พร้อมขาย";
  if (s === "unavailable") return "ไม่พร้อมขาย";
  return s || "-";
}

function mapRow(it) {
  // รองรับ field หลายแบบ
  const productStatus = it?.product_status || it?.status || it?.productStatus || "";
  const modelObj = it?.model || {};
  const categoryObj = modelObj?.category || it?.category || {};
  const productName = asText(it?.product_name).trim();
  const categoryName = asText(categoryObj?.name);
  const modelName = asText(modelObj?.name || it?.model);
  const itemName =
    productName ||
    (categoryName && modelName ? `${categoryName} ${modelName}` : categoryName || modelName) ||
    asText(it?.item || it?.item_name || it?.name) ||
    "-";
  const createdByUsername =
    it?.created_by_user?.username ||
    it?.createdByUser?.username ||
    it?.created_by_user?.data?.username ||
    it?.created_by_user?.data?.attributes?.username ||
    it?.createdByUser?.data?.username ||
    it?.createdByUser?.data?.attributes?.username ||
    "";

  return {
    id: it?.id,
    status: statusLabelFromProductStatus(productStatus),

    item: itemName,
    cap: asText(it?.capacity?.size || it?.capacity?.name || it?.capacity || it?.storage || "-"),
    color: asText(it?.color?.name || it?.color || "-"),
    quality: asText(it?.quality_master?.name || it?.quality_master || it?.quality || it?.condition || it?.desc || "-"),
    gear: asText(it?.equipments || it?.gear || it?.accessory?.name || "-"),
    warranty: asText(it?.warranty_info || it?.warranty_status || it?.warranty || "-"),
    imei: asText(it?.imei || it?.IMEI || it?.serial || "-"),
    buyer: asText(createdByUsername || it?.buyer || it?.buyer_name || it?.receiver || "-"),
  };
}

export default function PricingQueuePage() {
  const http = useMemo(() => createHttpClient(), []);

  const [query, setQuery] = useState("");
  const [range, setRange] = useState([null, null]);
  const [statusFilter, setStatusFilter] = useState(""); // "", "พร้อมขาย", "ไม่พร้อมขาย"

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  // ✅ server data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSort = (key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir("asc");
      return key;
    });
  };

  // ✅ fetch: products เฉพาะ available + unavailable
  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError("");

        const [start, end] = range || [];
        const s = start ? new Date(start) : null;
        const e = end ? new Date(end) : null;
        if (s) s.setHours(0, 0, 0, 0);
        if (e) e.setHours(23, 59, 59, 999);

        // ถ้า backend รองรับ pagination/filter ฝั่ง server คุณจะส่งเพิ่มได้
        const res = await http.get("/products", {
          params: {
            ...(s && e ? { "filters[createdAt][$gte]": s.toISOString() } : {}),
            ...(s && e ? { "filters[createdAt][$lte]": e.toISOString() } : {}),
            // ถ้า backend รองรับค้นหา server-side:
            // ...(query.trim() ? { q: query.trim() } : {}),
          },
          signal: ac.signal,
        });

        const list = res?.data?.data ?? [];
        const allowed = new Set(["draft"]);

        const filtered = (Array.isArray(list) ? list : []).filter((it) =>
          allowed.has(String(it?.product_status || "").toLowerCase())
        );

        const mapped = filtered.map(mapRow);
        if (!alive) return;
        setRows(mapped);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.name === "AbortError") return;
        console.error(e);
        if (!alive) return;
        setError("โหลดข้อมูลไม่สำเร็จ");
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [http, range?.[0], range?.[1]]); // ✅ reload เมื่อเปลี่ยนช่วงวันที่

  // ✅ filter (client) — query + status
  const filtered = useMemo(() => {
    let next = [...rows];

    if (statusFilter) next = next.filter((r) => r.status === statusFilter);

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      next = next.filter((r) =>
        [r.item, r.cap, r.color, r.quality, r.imei, r.buyer].some((x) =>
          String(x || "").toLowerCase().includes(q)
        )
      );
    }

    return next;
  }, [rows, query, statusFilter]);

  // ✅ sort
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const next = [...filtered].sort((a, b) => {
      const c = compareText(a?.[sortKey], b?.[sortKey]);
      return sortDir === "asc" ? c : -c;
    });
    return next;
  }, [filtered, sortKey, sortDir]);

  // ✅ pagination
  const totalItems = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.max(1, Math.min(page, totalPages));

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePage, pageSize]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, statusFilter, range]);

  const columns = useMemo(
    () => [
      {
        key: "status",
        title: "สถานะออเดอร์",
        headerClassName: "w-[150px]",
        render: (r) => <StatusPill value={r.status} />,
      },
      {
        key: "item",
        title: "รายการ",
        headerClassName: "w-[250px]",
        className: "font-semibold",
        sortable: true,
        sortKey: "item",
      },
      { key: "cap", title: "ความจุ" },
      { key: "color", title: "สี", headerClassName: "w-[100px]" },
      { key: "quality", title: "คุณภาพสินค้า", headerClassName: "w-[200px]" },
      { key: "gear", title: "อุปกรณ์" },
      {
        key: "warranty",
        title: "ประกันศูนย์",
        sortable: true,
        sortKey: "warranty",
      },
      { key: "imei", title: "IMEI", headerClassName: "w-[150px]" },
      { key: "buyer", title: "ผู้รับซื้อ" },
      {
        key: "manage",
        title: "จัดการ",
        render: (r) => (
          <NavLink
            to={{
              pathname: `/app/products/stock-buy-in/${encodeURIComponent(r.id)}`,
            }}
            state={{
              fromPricingQueue: true,
              returnTo: "/app/pricing-queue",
            }}
            className="inline-flex items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#047DED] active:scale-[0.98] hover:bg-[#F5F5F5] hover:border-none"
          >
            จัดการ
          </NavLink>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-4 p-4 bg-white min-h-full">
      <PageHeader
        icon={Home}
        breadcrumb="หน้าหลัก"
        title="รายการสินค้ารอกำหนดราคา"
        subtitle=""
        showExport={false}
        exportPath="/export/pricing-queue"
      />

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {/* toolbar */}
      <div className="rounded-2xl bg-[#F5F5F5] p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="w-full md:max-w-[520px]">
            <div className="flex items-center gap-3 rounded-xl border hover:border-2 border-[#1D161633] bg-white px-4 py-3">
              <Search size={18} className="text-[#1D1616B2]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680]"
                placeholder="ค้นหารายการ..."
              />
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center justify-end gap-2 md:w-auto md:justify-end">
            <DateRangeButton mode="single" range={range} setRange={setRange} />
            <StatusSelect value={statusFilter} onChange={setStatusFilter} />
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={pageRows}
        rowHeight="h-16"
        headerBg="bg-slate-50"
        striped={false}
        tableMinWidth="min-w-[1100px]"
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={onSort}
        loading={loading}
      />

      <PaginationFooter
        totalItems={totalItems}
        page={safePage}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        pageSizeOptions={[50, 100, 200]}
      />
    </div>
  );
}